import { Resources } from 'constants/resources';
import { Role } from 'constants/roles';

import React, { useMemo, useRef } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useCreateCommission } from 'hooks/queries/useCreateCommission';
import { useCreatePathWithQuery } from 'hooks/useCreatePathWithQuery';
import { useGoBack } from 'hooks/useGoBack';
import { useNavigateToRoot } from 'hooks/useNavigateToRoot';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { COMMISSIONS_SCHEMA } from 'pages/Commissions/schemas/schema';
import { transform } from 'pages/Commissions/utils/transform';
import { queryClient } from 'providers/queryClient';
import { HttpError, SimpleForm, useNotify } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CancelButton, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CreatePage } from 'shared/react-admin/Pages/CreatePage/CreatePage';
import { CommissionsCreateApprovedSuccessDialog } from 'shared/widgets/CommissionsCreateApprovedSuccessDialog/CommissionsCreateApprovedSuccessDialog';
import { CommissionsCreateSuccessDialog } from 'shared/widgets/CommissionsCreateSuccessDialog/CommissionsCreateSuccessDialog';
import { Commission } from 'types/embedded/commission';
import { getSubmissionErrors } from 'utils/getSubmissionErrors';

import {
  CommissionsCreateNewVersionForm,
  CommissionsCreateNewVersionFormValues,
} from './CommissionsCreateNewVersionForm/CommissionsCreateNewVersionForm';

export const CommissionsCreateNewVersion: React.FC = () => {
  const [searchParams] = useSearchParams();

  const defaultValues = useMemo(
    () => ({
      parent: searchParams.get('parent'),
      provider: searchParams.get('provider'),
      merchant: searchParams.get('merchant'),
      transactionTypes: searchParams.getAll('transactionTypes'),
      currency: searchParams.get('currency'),
    }),
    [searchParams],
  );

  const { roles } = usePermissions();

  const target = useRef<'created' | undefined>();

  const { goBack } = useGoBack();
  const { toRoot } = useNavigateToRoot();
  const notify = useNotify();
  const { createPathWithQuery } = useCreatePathWithQuery();
  const navigate = useNavigate();

  const modalApproved = useRecordModal<Commission>();
  const modalNew = useRecordModal<Commission>();

  const { createCommissionAsync } = useCreateCommission();

  const onSubmit = async (data: CommissionsCreateNewVersionFormValues) => {
    const transformedData = transform(data);

    try {
      const result = await createCommissionAsync({
        data: transformedData,
        targetStatus: target.current,
      });

      await queryClient.invalidateQueries({ queryKey: [Resources.Commissions] });

      target.current
        ? modalNew.handleOpenRecordModal(result)
        : modalApproved.handleOpenRecordModal(result);
    } catch (error) {
      notify(`Ошибка: ${(error as Error)?.message || 'ошибка сервера'}`, {
        type: 'error',
        autoHideDuration: 3000,
      });

      return getSubmissionErrors(error as HttpError);
    }
  };
  return (
    <CreatePage listName="Комиссии" resource="commissions" title="Создание новой версии комиссии">
      <SimpleForm
        defaultValues={defaultValues}
        onSubmit={onSubmit as any}
        resolver={yupResolver<FieldValues>(COMMISSIONS_SCHEMA)}
        toolbar={false}
      >
        <CommissionsCreateNewVersionForm />
        <CardActionsButtons
          rightActionsSlot={[
            <CancelButton key="cancel-button" onClick={goBack} variant="outlined" />,
            <SaveButton
              key="create-button"
              label="Создать"
              onClick={() => (target.current = 'created')}
            />,
            roles?.includes(Role.Admin) && (
              <SaveButton
                key="save-button"
                label="Создать и согласовать"
                onClick={() => (target.current = undefined)}
              />
            ),
          ]}
          sx={{ marginTop: 'auto', padding: 0 }}
        />
      </SimpleForm>
      <CommissionsCreateApprovedSuccessDialog
        commissionName={modalApproved.record?.name || ''}
        onClose={() => {
          toRoot();
          modalApproved.handleCloseRecordModal();
        }}
        open={modalApproved.openRecordModal}
      />
      <CommissionsCreateSuccessDialog
        commissionName={modalNew.record?.name || ''}
        onAgreement={() =>
          navigate(
            createPathWithQuery({
              resource: Resources.Commissions,
              id: modalNew.record?.['@id'],
              type: 'show',
            }),
          )
        }
        onClose={() => {
          modalNew.handleCloseRecordModal();
          toRoot();
        }}
        open={modalNew.openRecordModal}
      />
    </CreatePage>
  );
};
